import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AdminRecording,
  getAllRecordings,
  setRecordingApproval,
  deleteRecording,
  getAudioUrl,
  VoiceScope,
} from '../api/voice';
import { raceLink } from '../api/feeds';
import './AdminNotes.css';

// MediaRecorder webm/ogg blobs have no duration header; force a seek so the
// player shows the real length instead of "0:00 / 0:00".
const fixAudioDuration = (e: React.SyntheticEvent<HTMLAudioElement>) => {
  const el = e.currentTarget;
  if (el.duration === Infinity || Number.isNaN(el.duration)) {
    el.currentTime = 1e101;
    el.ontimeupdate = () => {
      el.ontimeupdate = null;
      el.currentTime = 0;
    };
  }
};

const fmtWhen = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const statusLabel: Record<string, string> = {
  transcribing: 'Transcribing…',
  transcribed: 'Transcribed',
  empty: 'No speech',
  failed: 'Failed',
  awaiting_key: 'Awaiting key',
};

// A human-readable label for where a note was taken.
const scopeContext = (r: AdminRecording): string => {
  if (r.scope === 'horse') return r.horse_name ? `Horse · ${r.horse_name}` : 'Horse';
  if (r.scope === 'race') {
    const bits = [r.meeting_name, r.race_number != null ? `R${r.race_number}` : null]
      .filter(Boolean)
      .join(' ');
    return bits ? `Race · ${bits}` : 'Race';
  }
  return 'Session';
};

const AdminNotes: React.FC = () => {
  const [items, setItems] = useState<AdminRecording[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userFilter, setUserFilter] = useState<string>('all');
  const [scopeFilter, setScopeFilter] = useState<VoiceScope | 'all'>('all');
  const [actingId, setActingId] = useState<string | null>(null);

  const toggleApproval = async (it: AdminRecording) => {
    setActingId(it.id);
    setError(null);
    try {
      await setRecordingApproval(it.id, !it.approved);
      setItems((prev) =>
        prev.map((r) => (r.id === it.id ? { ...r, approved: !it.approved } : r))
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to update approval');
    } finally {
      setActingId(null);
    }
  };

  const removeNote = async (it: AdminRecording) => {
    if (!window.confirm('Delete this voice note for good? This cannot be undone.')) return;
    setActingId(it.id);
    setError(null);
    try {
      await deleteRecording(it.id);
      setItems((prev) => prev.filter((r) => r.id !== it.id));
    } catch (err: any) {
      setError(err?.message || 'Failed to delete note');
    } finally {
      setActingId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAllRecordings()
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load notes');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Distinct usernames for the filter dropdown.
  const usernames = useMemo(() => {
    const set = new Set<string>();
    items.forEach((it) => {
      if (it.username) set.add(it.username);
    });
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(
    () =>
      items.filter(
        (it) =>
          (userFilter === 'all' || it.username === userFilter) &&
          (scopeFilter === 'all' || it.scope === scopeFilter)
      ),
    [items, userFilter, scopeFilter]
  );

  return (
    <div className="admin-notes-page">
      <h1>All voice notes</h1>
      <p className="admin-notes-intro">
        Admin view of every user's voice notes across the platform.{' '}
        {items.length > 0 && (
          <span className="admin-notes-count">
            {filtered.length} of {items.length} shown
          </span>
        )}
      </p>

      {error && <div className="admin-notes-error">{error}</div>}

      <div className="admin-notes-filters">
        <label className="admin-notes-filter">
          <span>User</span>
          <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
            <option value="all">All users</option>
            {usernames.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
        <label className="admin-notes-filter">
          <span>Scope</span>
          <select
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value as VoiceScope | 'all')}
          >
            <option value="all">All scopes</option>
            <option value="session">Session</option>
            <option value="race">Race</option>
            <option value="horse">Horse</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div className="admin-notes-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="admin-notes-empty">No voice notes match these filters.</div>
      ) : (
        <ul className="admin-notes-list">
          {filtered.map((it) => (
            <li key={it.id} className="admin-note">
              <div className="admin-note-head">
                <span className="admin-note-user">{it.username || 'Unknown user'}</span>
                {it.scope === 'race' && it.meeting_name && it.race_number != null ? (
                  <Link
                    className="admin-note-scope admin-note-scope--link"
                    to={raceLink(it.meeting_name, it.race_number, it.date || undefined)}
                    title="Open this race"
                  >
                    {scopeContext(it)}
                  </Link>
                ) : (
                  <span className="admin-note-scope">{scopeContext(it)}</span>
                )}
                {it.approved && (
                  <span className="admin-note-badge admin-note-badge--shared">Shared</span>
                )}
                <span className="admin-note-spacer" />
                <span className={`admin-note-badge admin-note-badge--${it.status}`}>
                  {statusLabel[it.status] || it.status}
                </span>
                <span className="admin-note-when">{fmtWhen(it.timestamp)}</span>
              </div>
              {it.transcript ? (
                <p className="admin-note-transcript">{it.transcript}</p>
              ) : it.error ? (
                <p className="admin-note-transcript admin-note-transcript--err">{it.error}</p>
              ) : (
                <p className="admin-note-transcript admin-note-transcript--muted">
                  (no transcript)
                </p>
              )}
              <audio
                className="admin-note-audio"
                controls
                preload="metadata"
                src={getAudioUrl(it.id)}
                onLoadedMetadata={fixAudioDuration}
              />
              <div className="admin-note-actions">
                <button
                  className={`admin-note-btn ${
                    it.approved ? 'admin-note-btn--unshare' : 'admin-note-btn--share'
                  }`}
                  onClick={() => toggleApproval(it)}
                  disabled={actingId === it.id}
                >
                  {actingId === it.id
                    ? '…'
                    : it.approved
                    ? 'Unshare'
                    : 'Approve & share'}
                </button>
                <button
                  className="admin-note-btn admin-note-btn--del"
                  onClick={() => removeNote(it)}
                  disabled={actingId === it.id}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminNotes;
